"use client";

import { useState } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthenticationContext";
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuList,
    NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Moon, Sun, UserCircle, Menu, X, ChevronRight } from "lucide-react";
import { FeedbackPanel } from "@/components/feedback/FeedbackPanel";
import { Loading } from "@/components/ui/Loading";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import versionData from "../../app/version.json";
import CustomButton from "../customButton/page";
import { ContactUsPageURL } from "@/lib/appConstants";
import Image from "next/image";

export function MarketingNavigation() {
    const router = useRouter();
    const pathname = usePathname();
    const { theme, toggleTheme } = useTheme();
    const { user, isLoading, signOut } = useAuth();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleSignOut = async () => {
        try {
            await signOut();
            router.replace("/auth/signin");
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
    };

    return (
        <nav className="fixed w-[calc(100%-2rem)] max-w-[93%] top-6 left-1/2 -translate-x-1/2 md:w-full md:max-w-[80rem] z-50 rounded-[3rem] border-b bg-background/90 dark:bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/60 print:hidden">
            <div className="flex h-14 items-center justify-between px-3 md:px-9 min-w-0">
                <button
                    onClick={() => router.push("/")}
                    className="flex items-center space-x-2 font-bold mr-2 md:mr-6 flex-shrink-0"
                    title={"Story Stream - Version " + versionData.version}
                >
                    <Image
                        src="/favicon.ico"
                        alt="Story Stream"
                        width={24}
                        height={24}
                        priority={true}
                        className="mr-1"
                    />
                    <span className="text-base md:text-lg font-light uppercase tracking-widest truncate">
                        Story Stream
                    </span>
                </button>

                <div className="hidden md:flex flex-1 justify-center items-center gap-10">
                    <Link
                        className={`hover:underline text-sm ${
                            pathname === "/books"
                                ? "font-bold text-foreground"
                                : "text-black dark:text-white"
                        }`}
                        href={`/books`}
                    >
                        My Books
                    </Link>
                    <Link
                        className={`hover:underline text-sm ${
                            pathname === "/checkout"
                                ? "font-bold text-foreground"
                                : "text-black dark:text-white"
                        }`}
                        href={`/checkout`}
                    >
                        Pricing
                    </Link>
                    <NavigationMenu>
                        <NavigationMenuList>
                            <NavigationMenuItem>
                                <NavigationMenuTrigger
                                    className={`hover:underline text-sm bg-transparent ${
                                        [
                                            ContactUsPageURL,
                                            "/blogs",
                                            "/docs/general/faqs",
                                        ].includes(pathname)
                                            ? "font-bold text-foreground"
                                            : "text-black dark:text-white"
                                    } mr-4`}
                                >
                                    More
                                </NavigationMenuTrigger>
                                <NavigationMenuContent className="bg-transparent border-b">
                                    <ul className="w-48 p-2">
                                        <li>
                                            <Link
                                                href="/docs"
                                                className="block px-4 py-2 text-sm hover:bg-accent rounded-md"
                                            >
                                                Documentation
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                href={ContactUsPageURL}
                                                className="block px-4 py-2 text-sm hover:bg-accent rounded-md"
                                            >
                                                Contact Us
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                href="/blogs"
                                                className="block px-4 py-2 text-sm hover:bg-accent rounded-md"
                                            >
                                                Blogs
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                href="/docs/general/faqs"
                                                className="block px-4 py-2 text-sm hover:bg-accent rounded-md"
                                            >
                                                FAQ
                                            </Link>
                                        </li>
                                    </ul>
                                </NavigationMenuContent>
                            </NavigationMenuItem>
                        </NavigationMenuList>
                    </NavigationMenu>
                </div>

                <div className="flex items-center space-x-2 flex-shrink-0">
                    {!isLoading ? (
                        user ? (
                            <NavigationMenu>
                                <NavigationMenuList>
                                    <NavigationMenuItem>
                                        <NavigationMenuTrigger className="hidden md:flex items-center space-x-2 max-w-[12rem]">
                                            <UserCircle className="h-6 w-6" />
                                            <span className="text-sm hidden md:inline-block truncate">
                                                {user.email}
                                            </span>
                                        </NavigationMenuTrigger>
                                        <NavigationMenuContent>
                                            <ul className="w-48 p-2">
                                                <li>
                                                    <button
                                                        onClick={handleSignOut}
                                                        className="block px-4 py-2 text-sm hover:bg-accent rounded-md"
                                                    >
                                                        Sign Out
                                                    </button>
                                                </li>
                                            </ul>
                                        </NavigationMenuContent>
                                    </NavigationMenuItem>
                                </NavigationMenuList>
                            </NavigationMenu>
                        ) : (
                            <button
                                onClick={() => router.push("/auth/signin")}
                                className="text-sm hidden md:block"
                            >
                                Sign In
                            </button>
                        )
                    ) : (
                        <Loading
                            text="Signing in..."
                            className="text-sm italic hidden md:block"
                            widthClass="w-32"
                        />
                    )}

                    <div className="hidden md:block">
                        <CustomButton
                            label="Get Started"
                            bg
                            icon={<ChevronRight />}
                            iconPosition="right"
                        />
                    </div>

                    <div className="hidden md:block">
                        <TooltipProvider delayDuration={0}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={toggleTheme}
                                        className="inline-flex items-center justify-center rounded-md h-10 w-10 hover:text-accent-foreground"
                                        aria-label="Toggle theme"
                                    >
                                        {theme === "light" ? (
                                            <Moon className="h-5 w-5" />
                                        ) : (
                                            <Sun className="h-5 w-5" />
                                        )}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>Change Theme</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>

                    <div className="hidden md:block">
                        <FeedbackPanel
                            locationElement="TopNavFeedbackButton"
                            iconOnly
                            tooltipText="Feedback"
                        />
                    </div>

                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="md:hidden ml-2"
                    >
                        {mobileMenuOpen ? (
                            <X className="h-6 w-6" />
                        ) : (
                            <Menu className="h-6 w-6" />
                        )}
                    </button>
                </div>
            </div>

            {mobileMenuOpen && (
                <div className="md:hidden px-6 pb-4 mb-3 space-y-8 ml-4 text-[16px]">
                    <button
                        onClick={() => {
                            closeMobileMenu();
                            router.push("/books");
                        }}
                        className={`block text-sm w-full text-left hover:underline ${
                            pathname === "/books" ? "font-semibold" : ""
                        }`}
                    >
                        My Books
                    </button>
                    <button
                        onClick={() => {
                            closeMobileMenu();
                            router.push("/checkout");
                        }}
                        className={`block text-sm w-full text-left hover:underline ${
                            pathname === "/checkout" ? "font-semibold" : ""
                        }`}
                    >
                        Pricing
                    </button>
                    <button
                        onClick={() => {
                            closeMobileMenu();
                            router.push("/features");
                        }}
                        className={`block text-sm w-full text-left hover:underline ${
                            pathname === "/features" ? "font-semibold" : ""
                        }`}
                    >
                        Features
                    </button>
                    {!isLoading ? (
                        user ? (
                            <>
                                <p className="text-sm mt-2 ">{user.email}</p>
                                <button
                                    onClick={() => {
                                        closeMobileMenu();
                                        handleSignOut();
                                    }}
                                    className="text-sm w-full text-left hover:underline"
                                >
                                    Sign Out
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => {
                                    closeMobileMenu();
                                    router.push("/auth/signin");
                                }}
                                className="text-sm w-full text-left hover:underline"
                            >
                                Sign In
                            </button>
                        )
                    ) : (
                        <Loading
                            text="Signing in..."
                            className="text-sm italic"
                            widthClass="w-32"
                        />
                    )}
                    
                    {/* Theme Toggle Button */}
                    <button
                        onClick={() => {
                            closeMobileMenu();
                            toggleTheme();
                        }}
                        className="flex items-center space-x-3 text-sm w-full text-left hover:underline"
                    >
                        {theme === "light" ? (
                            <Moon className="h-5 w-5" />
                        ) : (
                            <Sun className="h-5 w-5" />
                        )}
                        <span>Change Theme</span>
                    </button>
                    
                    {/* Feedback Button */}
                    <div className="w-full">
                        <FeedbackPanel
                            locationElement="MobileNavFeedbackButton"
                            iconOnly={false}
                        />
                    </div>
                </div>
            )}
        </nav>
    );
}
